import { RouterView, type RouteRecordRaw } from 'vue-router';
import {
  requireAnyType,
  requireClient,
  requireNotFederated,
  requireSupplier,
} from '@/routes/guards/orgtype';
import { requireReviewSharingEnabled } from '@/routes/guards/reviewSharing';
import Permission from '@/modules/current-user/permissions/permissions';

export default [
  {
    path: 'settings',
    children: [
      {
        path: '',
        name: 'common-settings',
        component: () => import('./Settings.vue'),
        redirect: { name: 'settings-account' },
        children: [
          {
            path: 'account',
            name: 'settings-account',
            components: {
              hero: () => import('./components/AccountHero.vue'),
              default: () => import('./components/Account.vue'),
            },
          },
          {
            path: 'appearance',
            name: 'settings-appearance',
            components: {
              hero: () => import('./components/AppearanceHero.vue'),
              default: () => import('./components/Appearance.vue'),
            },
          },
          {
            path: 'notifications',
            name: 'settings-notifications',
            beforeEnter: requireNotFederated,
            components: {
              hero: () => import('@/modules/notification-settings/NotificationSettingsHero.vue'),
              default: () => import('@/modules/notification-settings/NotificationSettings.vue'),
            },
          },
          {
            path: 'organisation',
            name: 'settings-organisation',
            components: {
              hero: () => import('./components/OrgSettingsHero.vue'),
              default: () => import('./components/OrgSettings.vue'),
            },
          },
          {
            path: 'admin',
            components: {
              hero: () => import('./components/UsersAndRolesHero.vue'),
              tabsBelowHero: () => import('./components/UsersAndRolesTabs.vue'),
              actions: () => import('./components/UsersAndRolesActions.vue'),
              default: RouterView,
              filters: RouterView,
              pagination: RouterView,
            },
            // Each tab fills the filters and pagination slots with its own views
            props: { filters: { name: 'filters' }, pagination: { name: 'pagination' } },
            meta: {
              dataTable: true,
            },
            children: [
              { path: '', name: 'settings-admin', redirect: { name: 'settings-admin-users' } },
              {
                path: 'users',
                name: 'settings-admin-users',
                components: {
                  default: () => import('./components/ManageUsers.vue'),
                  filters: () => import('./components/ManageUsersFilters.vue'),
                  pagination: () => import('./components/ManageUsersPagination.vue'),
                },
              },
              {
                path: 'roles',
                name: 'settings-admin-roles',
                components: {
                  default: () => import('./components/ManageRoles.vue'),
                  filters: () => import('./components/ManageRolesFilters.vue'),
                  pagination: () => import('./components/ManageRolesPagination.vue'),
                },
              },
            ],
          } as RouteRecordRaw,
          {
            path: 'sso',
            name: 'settings-sso',
            components: {
              hero: () => import('@/modules/auth-ssosettings/SSOSettingsHero.vue'),
              default: () => import('@/modules/auth-ssosettings/SSOSettings.vue'),
            },
          },
          {
            path: 'labels',
            name: 'settings-labels',
            beforeEnter: [requireAnyType(['client', 'federated'])],
            components: {
              hero: () => import('@/modules/labels/components/LabelListHero.vue'),
              default: () => import('@/modules/labels/components/LabelList.vue'),
              filters: () => import('@/modules/labels/components/LabelListFilters.vue'),
              pagination: () => import('@/modules/labels/components/LabelListPagination.vue'),
            },
            meta: {
              dataTable: true,
              sectionPerScope: true,
              permissions: [Permission.ClientsLabelsManage, Permission.FederatedLabelsManage],
            },
          } as RouteRecordRaw,
          {
            path: 'properties',
            name: 'settings-custom-properties',
            beforeEnter: [requireAnyType(['client', 'federated'])],
            components: {
              hero: () => import('@/modules/custom-properties/CustomPropertiesSettingsHero.vue'),
              default: () => import('@/modules/custom-properties/CustomPropertiesSettings.vue'),
              filters: () =>
                import('@/modules/custom-properties/CustomPropertiesSettingsFilters.vue'),
              pagination: () =>
                import('@/modules/custom-properties/CustomPropertiesSettingsPagination.vue'),
            },
            meta: {
              dataTable: true,
              sectionPerScope: true,
              permissions: [
                Permission.ClientsCustomPropertiesSettings,
                Permission.FederatedCustomPropertiesSettings,
              ],
            },
          } as RouteRecordRaw,
          {
            path: 'framework',
            name: 'settings-framework-defaults',
            beforeEnter: [requireClient],
            components: {
              hero: () => import('./components/FrameworkDefaultConfigHero.vue'),
              default: () => import('./components/FrameworkDefaultConfig.vue'),
            },
            meta: {
              permissions: [Permission.ClientsFrameworkConfigManage],
            },
          },
          {
            path: 'network-community',
            name: 'settings-network-community',
            beforeEnter: [requireClient],
            components: {
              hero: () => import('./components/NetworkAndCommunityHero.vue'),
              default: () => import('./components/NetworkAndCommunity.vue'),
            },
            meta: {
              permissions: [Permission.ClientsNetworkSettings],
            },
          },
          {
            path: 'review-sharing',
            name: 'settings-review-sharing',
            beforeEnter: [requireClient, requireReviewSharingEnabled],
            components: {
              hero: () => import('./components/ReviewSharingSettingsHero.vue'),
              default: () => import('./components/ReviewSharingSettings.vue'),
            },
            meta: {
              permissions: [Permission.ClientsReviewSharingManage],
            },
          },
          {
            path: 'api',
            name: 'settings-api',
            beforeEnter: [requireClient],
            components: {
              hero: () => import('@/modules/api-public/PublicAPISettingsHero.vue'),
              default: () => import('@/modules/api-public/PublicAPISettings.vue'),
              filters: () => import('@/modules/api-public/PublicAPISettingsFilters.vue'),
              pagination: () => import('@/modules/api-public/PublicAPISettingsPagination.vue'),
            },
            meta: {
              dataTable: true,
              permissions: [Permission.SettingsApiKeysManage],
            },
          } as RouteRecordRaw,
          {
            path: 'discussions',
            name: 'settings-discussions',
            beforeEnter: [
              requireClient, // also guards from federated org, since federated can't be client
            ],
            components: {
              hero: () => import('@/modules/discussions/DiscussionsConfigHero.vue'),
              default: () => import('@/modules/discussions/DiscussionsConfig.vue'),
            },
            meta: {
              permissions: [Permission.SettingsDiscussionSeen],
            },
          },
          {
            path: 'risks',
            name: 'settings-risks',
            beforeEnter: [requireClient],
            components: {
              hero: () => import('@/modules/risks/RisksSettingsHero.vue'),
              default: () => import('@/modules/risks/RisksSettings.vue'),
            },
            meta: {
              permissions: [Permission.ClientsRisksSettings],
            },
          },
          {
            path: 'products',
            name: 'settings-products',
            beforeEnter: [requireSupplier],
            components: {
              hero: () => import('@/modules/products/ProductsSettingsHero.vue'),
              default: () => import('@/modules/products/ProductsSettings.vue'),
            },
            meta: {
              permissions: [Permission.SuppliersAssessmentEdit],
            },
          },
        ],
      },
      // These routes are separate so that they don't have the sidebar or padding
      {
        path: 'admin/roles/new',
        name: 'settings-admin-new-role',
        component: () => import('./components/AddRole.vue'),
        meta: {
          permissions: [Permission.SettingsRolesManage],
        },
      },
      {
        path: 'admin/roles/:id',
        name: 'settings-admin-edit-role',
        component: () => import('./components/EditRole.vue'),
        props: true,
      },
    ],
  },
] satisfies RouteRecordRaw[];
